import React, { useState } from "react";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import colors from "../../Constants/colors";
import { useTranslation } from "react-i18next";
import { ComoInput } from "../Common/ComoInput";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";

interface NamespaceFieldProps {
  namespaces: string[];
  onChange: (namespaces: string[]) => void;
}

const NamespaceField: React.FC<NamespaceFieldProps> = ({
  namespaces,
  onChange,
}) => {
  const [namespaceInput, setNamespaceInput] = useState("");
  const { t } = useTranslation("workspaces");
  const { colors: workspace } = useWorkspaceColors();

  const handleAddNamespace = () => {
    if (!namespaceInput.trim()) return;

    onChange([...namespaces, namespaceInput.trim()]);
    setNamespaceInput("");
  };

  const handleRemoveNamespace = (namespace: string) => {
    onChange(namespaces.filter((ns) => ns !== namespace));
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1}>
        <ComoInput
          label={t("addNamespace")}
          name="namespaceInput"
          value={namespaceInput}
          onChange={(e) => setNamespaceInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAddNamespace();
            }
          }}
          InputLabelProps={{ shrink: true }}
        />
        <IconButton
          onClick={handleAddNamespace}
          sx={{
            backgroundColor: workspace.accent,
            color: workspace.primary_dark,
            borderRadius: 1.5,
            "&:hover": {
              backgroundColor: workspace.accent,
              opacity: 0.9,
            },
          }}
        >
          <AddIcon />
        </IconButton>
      </Stack>

      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
        {(Array.isArray(namespaces) ? namespaces : []).map(
          (namespace, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1.25,
                py: 0.5,
                borderRadius: 1.5,
                bgcolor: colors.gray50,
                border: `1px solid ${colors.gray200}`,
              }}
            >
              <Typography
                sx={{
                  color: colors.primary_dark,
                  fontSize: "0.85rem",
                }}
              >
                {namespace}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleRemoveNamespace(namespace)}
                sx={{ color: colors.gray400, p: 0.25 }}
              >
                <CloseIcon fontSize="inherit" sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ),
        )}
      </Stack>
    </Box>
  );
};

export default NamespaceField;
